/**
 * Bottom sheet / modal.
 *
 * Promise-based: `await openSheet({...})` resolves with the value passed
 * to `close(value)`. Used for the cost-confirmation step, import review
 * and other flows that must block before doing something expensive.
 */

import { h } from '../../utils/dom.js';

const root = () => document.getElementById('sheet-root');

/**
 * @param {object} options
 * @param {string} options.title
 * @param {Node|string|Array} options.body
 * @param {Array<{label:string, value?:any, kind?:string, close?:boolean}>} [options.actions]
 * @param {boolean} [options.dismissible]
 * @returns {Promise<any>}
 */
export function openSheet({ title, body, actions = [{ label: 'Close', kind: 'ghost' }], dismissible = true }) {
  const host = root();
  if (!host) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      resolve(value);
    };

    const actionButtons = actions.map((action) =>
      h(
        'button',
        {
          class: `btn ${action.kind === 'primary' ? 'btn--primary' : action.kind === 'danger' ? 'btn--danger' : 'btn--ghost'}`,
          type: 'button',
          onclick: () => finish(action.value === undefined ? action.label : action.value),
        },
        [action.label],
      ),
    );

    const sheet = h('div', { class: 'sheet', role: 'dialog', 'aria-modal': 'true', 'aria-label': title }, [
      h('div', { class: 'sheet__grabber' }),
      h('div', { class: 'sheet__head' }, [
        h('h3', {}, [title]),
        dismissible
          ? h(
              'button',
              { class: 'sheet__close', type: 'button', 'aria-label': 'Close', onclick: () => finish(null) },
              ['\u00d7'],
            )
          : null,
      ]),
      typeof body === 'string' ? h('div', {}, [body]) : body,
      h('div', { class: 'btn-row', style: { marginTop: '16px' } }, actionButtons),
    ]);

    const backdrop = h('div', { class: 'sheet-backdrop' }, [sheet]);
    if (dismissible) {
      backdrop.addEventListener('click', (event) => {
        if (event.target === backdrop) finish(null);
      });
    }

    const onKey = (event) => {
      if (event.key === 'Escape' && dismissible) finish(null);
    };
    document.addEventListener('keydown', onKey);

    host.append(backdrop);
    sheet.querySelector('button')?.focus?.();
  });
}

/** Simple confirm dialog. */
export async function confirmSheet({ title, message, confirmLabel = 'Confirm', kind = 'primary' }) {
  const result = await openSheet({
    title,
    body: h('p', { class: 'muted' }, [message]),
    actions: [
      { label: 'Cancel', value: false, kind: 'ghost' },
      { label: confirmLabel, value: true, kind },
    ],
  });
  return result === true;
}
